import { getPageBySlugs, getPageByPath, getPageById, getPagePreviewById, getTranslatedRoute, type WpPage } from "@/lib/wp";
import { extractSeoMeta, type WpSeo } from "@/lib/seo";

export type HomePageContent = {
  id: number;
  title: string;
  sections: HomePageSection[];
  alternateRoute?: string | null;
  seo?: WpSeo | null;
};

export type HomePageSection =
  | { type: "hero"; data: HomeHeroData }
  | { type: "services_grid"; data: HomeServicesData }
  | { type: "solutions_tabs"; data: HomeSolutionsTabsData }
  | { type: "one_platform"; data: HomeOnePlatformData }
  | { type: "growth"; data: HomeGrowthData }
  | { type: "integrations"; data: HomeIntegrationsData }
  | { type: "process_flow_tabs"; data: HomeProcessFlowTabsData }
  | { type: "hero_small_image"; data: HomeHeroSmallImageData }
  | { type: "hero_full_bg"; data: HomeHeroFullBgData }
  | { type: "page_hero"; data: HomePageHeroData }
  | { type: "section_title"; data: HomeSectionTitleData }
  | { type: "demo_video_card"; data: HomeDemoVideoCardData }
  | { type: "technical_feature"; data: HomeTechnicalFeatureData }
  | { type: "video_with_overlay_cards"; data: HomeVideoWithOverlayCardsData }
  | { type: "platform_mapping"; data: HomePlatformMappingData }
  | { type: "progress_steps"; data: HomeProgressStepsData }
  | { type: "featured_overlay_cards"; data: HomeFeaturedOverlayCardsData }
  | { type: "feature_cards_4col"; data: HomeFeatureCards4ColData }
  | { type: "feature_cards_3col"; data: HomeFeatureCards3ColData }
  | { type: "technical_features_2rows"; data: HomeTechnicalFeatures2RowsData }
  | { type: "technical_features_3rows"; data: HomeTechnicalFeatures3RowsData }
  | { type: "benefit_features_2rows"; data: HomeBenefitFeatures2RowsData }
  | { type: "partner_program"; data: HomePartnerProgramData }
  | { type: "icon_cards_4"; data: HomeServicesData }
  | { type: "bento_box_short"; data: HomeBentoBoxShortData }
  | { type: "bento_box_long"; data: HomeBentoBoxLongData };

export type HomeHeroData = {
  heading?: string;
  subheading?: string;
  primaryCta?: HomeCta | null;
  secondaryCta?: HomeCta | null;
  lottieSrc?: string | null;
  lottieAlt?: string | null;
  showAnimation: boolean;
};

export type HomeHeroSmallImageData = {
  imageUrl?: string;
  imageAlt?: string;
  heading?: string;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
};

export type HomeHeroFullBgData = {
  heading?: string;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  contained?: boolean;
  backgroundImageMobile?: string;
  backgroundImageSmallTablet?: string;
  backgroundImageTablet?: string;
  backgroundImageSmallDesktop?: string;
  backgroundImageDesktop?: string;
};

export type HomePageHeroData = {
  icon?: string;
  miniTitle?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  backgroundImageMobile?: string;
  backgroundImageSmallTablet?: string;
  backgroundImageTablet?: string;
  backgroundImageSmallDesktop?: string;
  backgroundImageDesktop?: string;
};

export type HomeSectionTitleData = {
  icon?: string;
  iconMiniTitle?: string;
  fadeMiniTitle?: string;
  title?: string;
  descriptionBold?: string;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
};

export type HomeDemoVideoCardData = {
  icon?: string;
  miniTitle?: string;
  heading?: string;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  posterUrl?: string;
  posterAlt?: string;
  videoUrl?: string;
};

export type HomeServicesData = {
  kicker?: string;
  heading?: string;
  subheading?: string;
  cards: HomeServiceCard[];
};

export type HomeServiceCard = {
  title?: string;
  description?: string;
  icon?: string;
  learnMoreUrl?: string | null;
};

export type HomeSolutionsTabsData = {
  kicker?: string;
  heading?: string;
  subheading?: string;
  tabs: HomeSolutionsTab[];
  viewMoreCta?: HomeCta | null;
};

export type HomeSolutionsTab = {
  id: string;
  label?: string;
  description?: string;
  cards: HomeSolutionsCard[];
};

export type HomeSolutionsCard = {
  title?: string;
  description?: string;
  icon?: string;
};

export type HomeOnePlatformData = {
  heading?: string;
  subheading?: string;
  intro?: string;
  products: HomeProductTab[];
};

export type HomeProductTab = {
  id: string;
  label?: string;
  icon?: string;
  title?: string;
  description?: string;
  tag?: string;
  image?: string;
};

export type HomeGrowthData = {
  heading?: string;
  paragraph?: string;
  cta?: HomeCta | null;
  pointLabel?: string;
  featureTitle?: string;
  featureDescription?: string;
  featureLottieSrc?: string | null;
  featureLottieAlt?: string | null;
  featureLink?: HomeCta | null;
};

export type HomeIntegrationsData = {
  kicker?: string;
  heading?: string;
  subheading?: string;
  cards: HomeIntegrationCard[];
  cta?: HomeCta | null;
};

export type HomeIntegrationCard = {
  id: string;
  name?: string;
  logo?: string;
  services: HomeIntegrationService[];
};

export type HomeIntegrationService = {
  label?: string;
  icon?: string;
  colorHex?: string;
  backgroundRgba?: string;
};

export type HomeCta = {
  label: string;
  url: string;
  showIcon?: boolean;
};

export type HomeProcessFlowTabsData = {
  heading?: string;
  subheading?: string;
  tabs: HomeProcessFlowTab[];
};

export type HomeProcessFlowTab = {
  id: string;
  icon?: string;
  label?: string;
  title?: string;
  diagramUrl?: string;
  steps: HomeProcessFlowStep[];
};

export type HomeProcessFlowStep = {
  description?: string;
  imageUrl?: string;
  desktopHeight?: number;
};

export type HomeTechnicalFeatureData = {
  heading?: string;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  imageSrc?: string;
  imageAlt?: string;
};

export type HomeVideoWithOverlayCardsCard = {
  icon?: string;
  title?: string;
  description?: string;
};

export type HomeVideoWithOverlayCardsData = {
  title?: string;
  subtitle?: string;
  videoId?: string;
  cards: HomeVideoWithOverlayCardsCard[];
  ctaLabel?: string;
  ctaUrl?: string;
};

export type HomePlatformMappingTab = {
  id: string;
  icon?: string;
  label?: string;
  diagramUrl?: string;
  explanationIcon?: string;
  explanationTitle?: string;
  explanationDescription?: string;
};

export type HomePlatformMappingData = {
  heading?: string;
  subheading?: string;
  tabs: HomePlatformMappingTab[];
};

export type HomeProgressStep = {
  title?: string;
  description?: string;
  icon?: string;
};

export type HomeProgressStepsData = {
  summaryImageSrc?: string;
  summaryImageAlt?: string;
  summaryTitle?: string;
  summaryDescription?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  steps: HomeProgressStep[];
};

export type HomeFeaturedOverlayCard = {
  image?: string;
  icon?: string;
  title?: string;
  description?: string;
  link?: string;
};

export type HomeFeaturedOverlayCardsData = {
  heading?: string;
  subheading?: string;
  cards: HomeFeaturedOverlayCard[];
};

export type HomeFeatureCard = {
  id: string;
  icon?: string;
  title?: string;
  description?: string;
  image?: string;
  href?: string;
};

export type HomeFeatureCards4ColData = {
  heading?: string;
  subheading?: string;
  description?: string;
  cards: HomeFeatureCard[];
};

export type HomeFeatureCards3ColData = {
  heading?: string;
  subheading?: string;
  description?: string;
  cards: HomeFeatureCard[];
};

export type HomeBentoBoxShortCard = {
  icon?: string;
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  link?: string;
};

export type HomeBentoBoxShortData = {
  heading?: string;
  subheading?: string;
  cards: HomeBentoBoxShortCard[];
  ctaLabel?: string;
  ctaUrl?: string;
};

export type HomeBentoBoxLongCard = {
  id: string;
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
};

export type HomeBentoBoxLongData = {
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  cards: HomeBentoBoxLongCard[];
};

export type HomeTechnicalFeatureRowPoint = {
  icon?: string;
  title?: string;
  description?: string;
};

export type HomeTechnicalFeatureRow = {
  pointLabel?: string;
  imageSrc?: string;
  imageAlt?: string;
  rowTitle?: string;
  rowDescription?: string;
  points: HomeTechnicalFeatureRowPoint[];
  learnMoreHref?: string;
  learnMoreLabel?: string;
};

export type HomeTechnicalFeatures2RowsData = {
  rows: HomeTechnicalFeatureRow[];
};

export type HomeTechnicalFeatures3RowsData = {
  rows: HomeTechnicalFeatureRow[];
};

export type HomeBenefitFeaturePoint = {
  icon?: string;
  title?: string;
  description?: string;
};

export type HomeBenefitFeaturesRow = {
  imageSrc?: string;
  imageAlt?: string;
  points: HomeBenefitFeaturePoint[];
};

export type HomeBenefitFeatures2RowsData = {
  rows: HomeBenefitFeaturesRow[];
};

export type HomePartnerProgramCard = {
  title?: string;
  description?: string;
  icon?: string;
  learnMoreUrl?: string;
};

export type HomePartnerProgramData = {
  kicker?: string;
  heading?: string;
  subheading?: string;
  cards: HomePartnerProgramCard[];
};

type RawLayoutEntry = {
  acf_fc_layout?: string;
  [key: string]: unknown;
};

type RawLayout = RawLayoutEntry[];

const TRUE_STRINGS = new Set(["1", "true", "TRUE", "yes", "on"]);

// Map of known page IDs for each language (Polylang creates separate pages per language)
// Note: WordPress configuration currently has English content for French pages
const HOMEPAGE_IDS: Record<string, number[]> = {
  en: [2],       // English homepage ID
  fr: [281, 2],  // French pages - currently showing English content due to WP config
};

const DEFAULT_HOME_SLUGS = [
  "onplexus-global-currency-orchestration-platform",
  "home",
  "homepage",
];
const LANG_DEFAULT_HOME_SLUGS: Record<string, string[]> = {
  fr: ["accueil"],
  en: ["onplexus-global-currency-orchestration-platform", "home"],
};

const DEFAULT_HOME_CACHE_KEY = "en";
type HomeCacheKey = string;
const HOME_CACHE = new Map<HomeCacheKey, HomePageContent>();
const HOME_PROMISES = new Map<HomeCacheKey, Promise<HomePageContent | null>>();

function homeCacheKey(lang?: string | null): HomeCacheKey {
  const normalized = (lang ?? DEFAULT_HOME_CACHE_KEY).trim().toLowerCase();
  return normalized || DEFAULT_HOME_CACHE_KEY;
}

export function getCachedHomePageContent(lang?: string | null): HomePageContent | null {
  const cached = HOME_CACHE.get(homeCacheKey(lang));
  return cached ?? null;
}

export function primeHomePageCache(lang: string | undefined, content: HomePageContent | null): void {
  if (!content) {
    return;
  }
  const key = homeCacheKey(lang);
  HOME_CACHE.set(key, content);
  HOME_PROMISES.delete(key);
}

function envHomeSlugs(lang?: string): string[] {
  const env = import.meta.env as Record<string, string | undefined>;
  const keys: string[] = [];
  if (lang) {
    const upper = lang.toUpperCase();
    keys.push(`VITE_WP_HOME_SLUGS_${upper}`, `VITE_WP_HOME_SLUG_${upper}`);
  }
  keys.push("VITE_WP_HOME_SLUGS", "VITE_WP_HOME_SLUG");

  const collected: string[] = [];
  for (const key of keys) {
    const raw = env[key]?.trim();
    if (!raw) continue;
    raw
      .split(/[,\s]+/)
      .map((slug) => slug.trim().toLowerCase())
      .filter(Boolean)
      .forEach((slug) => collected.push(slug));
  }
  return collected;
}

export function resolveHomePageSlugs(lang?: string): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];

  function push(slug: string) {
    const normalized = slug.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    ordered.push(normalized);
  }

  envHomeSlugs(lang).forEach(push);
  if (lang) {
    (LANG_DEFAULT_HOME_SLUGS[lang] ?? []).forEach(push);
  }
  DEFAULT_HOME_SLUGS.forEach(push);

  return ordered;
}

function asBoolean(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return TRUE_STRINGS.has(value.trim());
  return false;
}

function asString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return undefined;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function get(entry: RawLayoutEntry, key: string) {
  return entry[key];
}

function asId(value: unknown): string | undefined {
  const str = asString(value);
  return str ? str.toLowerCase() : undefined;
}

function mapHero(entry: RawLayoutEntry): HomeHeroData | null {
  const heading = asString(get(entry, "onp_h1"));
  const subheading = asString(get(entry, "onp_subheading"));
  const primaryLabel = asString(get(entry, "onp_cta_primary_label"));
  const primaryUrl = asString(get(entry, "onp_cta_primary_url"));
  const secondaryLabel = asString(get(entry, "onp_cta_secondary_label"));
  const secondaryUrl = asString(get(entry, "onp_cta_secondary_url"));
  const lottieSrc = asString(get(entry, "onp_lottie_src"));
  const lottieAlt = asString(get(entry, "onp_lottie_alt"));
  const showAnimation = asBoolean(get(entry, "onp_show_animation"));

  if (!heading && !subheading) return null;

  const data: HomeHeroData = {
    heading,
    subheading,
    primaryCta: primaryLabel && primaryUrl ? { label: primaryLabel, url: primaryUrl, showIcon: true } : null,
    secondaryCta: secondaryLabel && secondaryUrl ? { label: secondaryLabel, url: secondaryUrl, showIcon: false } : null,
    lottieSrc: lottieSrc ?? null,
    lottieAlt: lottieAlt ?? null,
    showAnimation,
  };

  return data;
}

function mapServices(entry: RawLayoutEntry): HomeServicesData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card) => {
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      const icon = asString(get(card, "onp_icon_emoji"));
      const learnMoreUrl = asString(get(card, "onp_learn_more_url"));
      if (!title && !description) return null;
      const cardData: HomeServiceCard = {
        title,
        description,
        icon,
        learnMoreUrl: learnMoreUrl ?? null,
      };
      return cardData;
    })
    .filter((card): card is HomeServiceCard => card !== null);

  if (cards.length === 0) return null;

  const section: HomeServicesData = {
    kicker: asString(get(entry, "onp_kicker")),
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    cards,
  };
  return section;
}

function mapSolutions(entry: RawLayoutEntry): HomeSolutionsTabsData | null {
  const tabsRawValue = get(entry, "onp_tabs");
  const tabsRaw = Array.isArray(tabsRawValue) ? (tabsRawValue as RawLayoutEntry[]) : [];
  const tabs = tabsRaw
    .map((tab) => {
      const id = asId(get(tab, "onp_tab_id"));
      const label = asString(get(tab, "onp_tab_label"));
      if (!id) return null;
      const cardsRawValue = get(tab, "onp_tab_cards");
      const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
      const cards = cardsRaw
        .map((card) => {
          const title = asString(get(card, "onp_title"));
          const description = asString(get(card, "onp_description"));
          const icon = asString(get(card, "onp_icon_emoji"));
          if (!title && !description) return null;
          const cardData: HomeSolutionsCard = { title, description, icon };
          return cardData;
        })
        .filter((card): card is HomeSolutionsCard => card !== null);

      const tabData: HomeSolutionsTab = {
        id,
        label,
        description: asString(get(tab, "onp_tab_description")),
        cards,
      };
      return tabData;
    })
    .filter((tab): tab is HomeSolutionsTab => tab !== null && tab.cards.length > 0);

  if (tabs.length === 0) return null;

  const viewMoreLabel = asString(get(entry, "onp_view_more_label"));
  const viewMoreUrl = asString(get(entry, "onp_view_more_url"));

  const section: HomeSolutionsTabsData = {
    kicker: asString(get(entry, "onp_kicker")),
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    tabs,
    viewMoreCta: viewMoreLabel && viewMoreUrl ? { label: viewMoreLabel, url: viewMoreUrl } : null,
  };
  return section;
}

function mapOnePlatform(entry: RawLayoutEntry): HomeOnePlatformData | null {
  const productsRawValue = get(entry, "onp_products");
  const productsRaw = Array.isArray(productsRawValue) ? (productsRawValue as RawLayoutEntry[]) : [];
  const products = productsRaw
    .map((product) => {
      const id = asId(get(product, "onp_product_id"));
      if (!id) return null;
      const productData: HomeProductTab = {
        id,
        label: asString(get(product, "onp_label_multiline")),
        icon: asString(get(product, "onp_icon_emoji")),
        title: asString(get(product, "onp_title")),
        description: asString(get(product, "onp_description")),
        tag: asString(get(product, "onp_tag")),
        image: asString(get(product, "onp_image")),
      };
      return productData;
    })
    .filter((product): product is HomeProductTab => product !== null);

  if (products.length === 0) return null;

  const section: HomeOnePlatformData = {
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    intro: asString(get(entry, "onp_intro")),
    products,
  };
  return section;
}

function mapGrowth(entry: RawLayoutEntry): HomeGrowthData | null {
  const heading = asString(get(entry, "onp_heading"));
  const paragraph = asString(get(entry, "onp_paragraph"));
  const ctaLabel = asString(get(entry, "onp_cta_label"));
  const ctaUrl = asString(get(entry, "onp_cta_url"));
  const featureLinkLabel = asString(get(entry, "onp_feature_link_label"));
  const featureLinkUrl = asString(get(entry, "onp_feature_link_url"));

  if (!heading && !paragraph) return null;

  const section: HomeGrowthData = {
    heading,
    paragraph,
    cta: ctaLabel && ctaUrl ? { label: ctaLabel, url: ctaUrl } : null,
    pointLabel: asString(get(entry, "onp_point_label")),
    featureTitle: asString(get(entry, "onp_feature_title")),
    featureDescription: asString(get(entry, "onp_feature_description")),
    featureLottieSrc: asString(get(entry, "onp_feature_lottie_src")) ?? null,
    featureLottieAlt: asString(get(entry, "onp_feature_lottie_alt")) ?? null,
    featureLink:
      featureLinkLabel && featureLinkUrl ? { label: featureLinkLabel, url: featureLinkUrl } : null,
  };
  return section;
}

function mapIntegrations(entry: RawLayoutEntry): HomeIntegrationsData | null {
  const cardsRawValue = get(entry, "onp_integrations");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card) => {
      const id = asId(get(card, "onp_id"));
      if (!id) return null;
      const servicesRawValue = get(card, "onp_services");
      const servicesRaw = Array.isArray(servicesRawValue) ? (servicesRawValue as RawLayoutEntry[]) : [];
      const services = servicesRaw
        .map((service) => {
          const label = asString(get(service, "onp_label"));
          const icon = asString(get(service, "onp_icon_emoji"));
          const color = asString(get(service, "onp_color_hex"));
          const bg = asString(get(service, "onp_bg_rgba"));
          if (!label && !icon) return null;
          const serviceData: HomeIntegrationService = {
            label,
            icon,
            colorHex: color,
            backgroundRgba: bg,
          };
          return serviceData;
        })
        .filter((service): service is HomeIntegrationService => service !== null);

      const cardData: HomeIntegrationCard = {
        id,
        name: asString(get(card, "onp_name")),
        logo: asString(get(card, "onp_logo")),
        services,
      };
      return cardData;
    })
    .filter((card): card is HomeIntegrationCard => card !== null);

  if (cards.length === 0) return null;

  const ctaLabel = asString(get(entry, "onp_all_integrations_label"));
  const ctaUrl = asString(get(entry, "onp_all_integrations_url"));

  const section: HomeIntegrationsData = {
    kicker: asString(get(entry, "onp_kicker")),
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    cards,
    cta: ctaLabel && ctaUrl ? { label: ctaLabel, url: ctaUrl } : null,
  };
  return section;
}

function mapProcessFlowTabs(entry: RawLayoutEntry): HomeProcessFlowTabsData | null {
  const tabsRawValue = get(entry, "onp_tabs");
  const tabsRaw = Array.isArray(tabsRawValue) ? (tabsRawValue as RawLayoutEntry[]) : [];

  const tabs = tabsRaw
    .map((tab, index) => {
      const stepsRawValue = get(tab, "onp_tab_steps");
      const stepsRaw = Array.isArray(stepsRawValue) ? (stepsRawValue as RawLayoutEntry[]) : [];
      const steps = stepsRaw
        .map((step) => {
          const description = asString(get(step, "onp_description"));
          const imageUrl = asString(get(step, "onp_image"));
          const desktopHeight = asNumber(get(step, "onp_desktop_height"));

          if (!description && !imageUrl) return null;

          const stepData: HomeProcessFlowStep = {
            description,
            imageUrl,
            desktopHeight,
          };
          return stepData;
        })
        .filter((step): step is HomeProcessFlowStep => step !== null);

      const id = asId(get(tab, "onp_tab_id")) ?? `tab-${index + 1}`;
      const icon = asString(get(tab, "onp_tab_icon"));
      const label = asString(get(tab, "onp_tab_label"));
      const title = asString(get(tab, "onp_tab_title"));
      const diagramUrl = asString(get(tab, "onp_tab_diagram"));

      if (!label && !title && steps.length === 0 && !diagramUrl) return null;

      const tabData: HomeProcessFlowTab = {
        id,
        icon,
        label,
        title,
        diagramUrl,
        steps,
      };
      return tabData;
    })
    .filter((tab): tab is HomeProcessFlowTab => tab !== null);

  if (tabs.length === 0) return null;

  const section: HomeProcessFlowTabsData = {
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    tabs,
  };

  return section;
}

function mapHeroSmallImage(entry: RawLayoutEntry): HomeHeroSmallImageData | null {
  const imageUrl = asString(get(entry, "onp_image"));
  const heading = asString(get(entry, "onp_heading"));
  const description = asString(get(entry, "onp_description"));
  const ctaLabel = asString(get(entry, "onp_cta_label"));
  const ctaUrl = asString(get(entry, "onp_cta_url"));

  if (!imageUrl && !heading && !description && !ctaLabel && !ctaUrl) return null;

  const section: HomeHeroSmallImageData = {
    imageUrl,
    imageAlt: asString(get(entry, "onp_image_alt")),
    heading,
    description,
    ctaLabel,
    ctaUrl,
  };

  return section;
}

function mapHeroFullBg(entry: RawLayoutEntry): HomeHeroFullBgData | null {
  const heading = asString(get(entry, "onp_heading"));
  const description = asString(get(entry, "onp_description"));
  const ctaLabel = asString(get(entry, "onp_cta_label"));
  const ctaUrl = asString(get(entry, "onp_cta_url"));
  const contained = asBoolean(get(entry, "onp_contained"));

  const anyBackground =
    asString(get(entry, "onp_bg_mobile")) ||
    asString(get(entry, "onp_bg_small_tablet")) ||
    asString(get(entry, "onp_bg_tablet")) ||
    asString(get(entry, "onp_bg_small_desktop")) ||
    asString(get(entry, "onp_bg_desktop"));

  if (!heading && !description && !ctaLabel && !ctaUrl && !anyBackground) return null;

  const section: HomeHeroFullBgData = {
    heading,
    description,
    ctaLabel,
    ctaUrl,
    contained,
    backgroundImageMobile: asString(get(entry, "onp_bg_mobile")),
    backgroundImageSmallTablet: asString(get(entry, "onp_bg_small_tablet")),
    backgroundImageTablet: asString(get(entry, "onp_bg_tablet")),
    backgroundImageSmallDesktop: asString(get(entry, "onp_bg_small_desktop")),
    backgroundImageDesktop: asString(get(entry, "onp_bg_desktop")),
  };

  return section;
}

function mapPageHero(entry: RawLayoutEntry): HomePageHeroData | null {
  const title = asString(get(entry, "onp_title"));
  const description = asString(get(entry, "onp_description"));
  const miniTitle = asString(get(entry, "onp_mini_title"));
  const icon = asString(get(entry, "onp_icon"));
  const ctaLabel = asString(get(entry, "onp_cta_label"));
  const ctaUrl = asString(get(entry, "onp_cta_url"));

  const anyBackground =
    asString(get(entry, "onp_bg_mobile")) ||
    asString(get(entry, "onp_bg_small_tablet")) ||
    asString(get(entry, "onp_bg_tablet")) ||
    asString(get(entry, "onp_bg_small_desktop")) ||
    asString(get(entry, "onp_bg_desktop"));

  if (!title && !description && !miniTitle && !icon && !ctaLabel && !ctaUrl && !anyBackground) {
    return null;
  }

  const section: HomePageHeroData = {
    icon,
    miniTitle,
    title,
    description,
    ctaLabel,
    ctaUrl,
    backgroundImageMobile: asString(get(entry, "onp_bg_mobile")),
    backgroundImageSmallTablet: asString(get(entry, "onp_bg_small_tablet")),
    backgroundImageTablet: asString(get(entry, "onp_bg_tablet")),
    backgroundImageSmallDesktop: asString(get(entry, "onp_bg_small_desktop")),
    backgroundImageDesktop: asString(get(entry, "onp_bg_desktop")),
  };

  return section;
}

function mapSectionTitle(entry: RawLayoutEntry): HomeSectionTitleData | null {
  const icon = asString(get(entry, "onp_icon_emoji")) ?? asString(get(entry, "onp_icon"));
  const iconMiniTitle = asString(get(entry, "onp_icon_mini_title"));
  const fadeMiniTitle = asString(get(entry, "onp_fade_mini_title"));
  const title = asString(get(entry, "onp_title"));
  const descriptionBold = asString(get(entry, "onp_description_bold"));
  const description = asString(get(entry, "onp_description"));
  const ctaLabel = asString(get(entry, "onp_cta_label"));
  const ctaUrl = asString(get(entry, "onp_cta_url"));

  if (
    !icon &&
    !iconMiniTitle &&
    !fadeMiniTitle &&
    !title &&
    !descriptionBold &&
    !description &&
    !ctaLabel &&
    !ctaUrl
  ) {
    return null;
  }

  const section: HomeSectionTitleData = {
    icon,
    iconMiniTitle,
    fadeMiniTitle,
    title,
    descriptionBold,
    description,
    ctaLabel,
    ctaUrl,
  };

  return section;
}

function mapDemoVideoCard(entry: RawLayoutEntry): HomeDemoVideoCardData | null {
  const icon = asString(get(entry, "onp_icon_emoji")) ?? asString(get(entry, "onp_icon"));
  const miniTitle = asString(get(entry, "onp_mini_title"));
  const heading = asString(get(entry, "onp_heading"));
  const description = asString(get(entry, "onp_description"));
  const ctaLabel = asString(get(entry, "onp_cta_label"));
  const ctaUrl = asString(get(entry, "onp_cta_url"));
  const posterUrl = asString(get(entry, "onp_poster"));
  const posterAlt = asString(get(entry, "onp_poster_alt"));
  const videoUrl = asString(get(entry, "onp_video_url"));

  if (
    !icon &&
    !miniTitle &&
    !heading &&
    !description &&
    !ctaLabel &&
    !ctaUrl &&
    !posterUrl &&
    !videoUrl
  ) {
    return null;
  }

  const section: HomeDemoVideoCardData = {
    icon,
    miniTitle,
    heading,
    description,
    ctaLabel,
    ctaUrl,
    posterUrl,
    posterAlt,
    videoUrl,
  };

  return section;
}

function mapBentoBoxShort(entry: RawLayoutEntry): HomeBentoBoxShortData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card) => {
      const icon = asString(get(card, "onp_icon"));
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      const image = asString(get(card, "onp_image"));
      const link = asString(get(card, "onp_link"));
      if (!icon && !title && !description && !image) return null;
      const cardData: HomeBentoBoxShortCard = {
        icon,
        title,
        description,
        image,
        imageAlt: asString(get(card, "onp_image_alt")),
        link,
      };
      return cardData;
    })
    .filter((card): card is HomeBentoBoxShortCard => card !== null);

  if (cards.length === 0 && !asString(get(entry, "onp_heading"))) return null;

  const section: HomeBentoBoxShortData = {
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    cards,
    ctaLabel: asString(get(entry, "onp_cta_label")),
    ctaUrl: asString(get(entry, "onp_cta_url")),
  };

  return section;
}

function mapBentoBoxLong(entry: RawLayoutEntry): HomeBentoBoxLongData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card, index) => {
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      const image = asString(get(card, "onp_image"));
      const imageAlt = asString(get(card, "onp_image_alt"));
      if (!title && !description && !image) return null;
      const cardData: HomeBentoBoxLongCard = {
        id: asId(get(card, "onp_id")) ?? `card-${index + 1}`,
        title,
        description,
        image,
        imageAlt,
      };
      return cardData;
    })
    .filter((card): card is HomeBentoBoxLongCard => card !== null);

  if (cards.length === 0 && !asString(get(entry, "onp_title"))) return null;

  const section: HomeBentoBoxLongData = {
    title: asString(get(entry, "onp_title")),
    description: asString(get(entry, "onp_description")),
    ctaLabel: asString(get(entry, "onp_cta_label")),
    ctaUrl: asString(get(entry, "onp_cta_url")),
    cards,
  };

  return section;
}

function mapTechnicalFeature(entry: RawLayoutEntry): HomeTechnicalFeatureData | null {
  const heading = asString(get(entry, "onp_heading"));
  const description = asString(get(entry, "onp_description"));
  const ctaLabel = asString(get(entry, "onp_cta_label"));
  const ctaUrl = asString(get(entry, "onp_cta_url"));
  const imageSrc = asString(get(entry, "onp_image"));
  const imageAlt = asString(get(entry, "onp_image_alt"));

  if (!heading && !description && !imageSrc && !ctaLabel && !ctaUrl) return null;

  const section: HomeTechnicalFeatureData = {
    heading,
    description,
    ctaLabel,
    ctaUrl,
    imageSrc,
    imageAlt,
  };

  return section;
}

function mapVideoOverlay(entry: RawLayoutEntry): HomeVideoWithOverlayCardsData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card) => {
      const icon = asString(get(card, "onp_icon"));
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      if (!icon && !title && !description) return null;
      const cardData: HomeVideoWithOverlayCardsCard = {
        icon,
        title,
        description,
      };
      return cardData;
    })
    .filter((card): card is HomeVideoWithOverlayCardsCard => card !== null);

  if (!cards.length && !asString(get(entry, "onp_title")) && !asString(get(entry, "onp_subtitle"))) {
    return null;
  }

  const section: HomeVideoWithOverlayCardsData = {
    title: asString(get(entry, "onp_title")),
    subtitle: asString(get(entry, "onp_subtitle")),
    videoId: asString(get(entry, "onp_video_id")),
    cards,
    ctaLabel: asString(get(entry, "onp_cta_label")),
    ctaUrl: asString(get(entry, "onp_cta_url")),
  };

  return section;
}

function mapPlatformMapping(entry: RawLayoutEntry): HomePlatformMappingData | null {
  const tabsRawValue = get(entry, "onp_tabs");
  const tabsRaw = Array.isArray(tabsRawValue) ? (tabsRawValue as RawLayoutEntry[]) : [];
  const tabs = tabsRaw
    .map((tab, index) => {
      const id = asId(get(tab, "onp_tab_id")) ?? `tab-${index + 1}`;
      const icon = asString(get(tab, "onp_tab_icon"));
      const label = asString(get(tab, "onp_tab_label"));
      const diagramUrl = asString(get(tab, "onp_tab_diagram"));
      const explanationIcon = asString(get(tab, "onp_explanation_icon"));
      const explanationTitle = asString(get(tab, "onp_explanation_title"));
      const explanationDescription = asString(get(tab, "onp_explanation_description"));

      if (!label && !diagramUrl && !explanationTitle && !explanationDescription) return null;

      const tabData: HomePlatformMappingTab = {
        id,
        icon,
        label,
        diagramUrl,
        explanationIcon,
        explanationTitle,
        explanationDescription,
      };
      return tabData;
    })
    .filter((tab): tab is HomePlatformMappingTab => tab !== null);

  if (tabs.length === 0) return null;

  const section: HomePlatformMappingData = {
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    tabs,
  };

  return section;
}

function mapProgressSteps(entry: RawLayoutEntry): HomeProgressStepsData | null {
  const stepsRawValue = get(entry, "onp_steps");
  const stepsRaw = Array.isArray(stepsRawValue) ? (stepsRawValue as RawLayoutEntry[]) : [];
  const steps = stepsRaw
    .map((step) => {
      const title = asString(get(step, "onp_title"));
      const description = asString(get(step, "onp_description"));
      const icon = asString(get(step, "onp_icon"));
      if (!title && !description && !icon) return null;
      const stepData: HomeProgressStep = {
        title,
        description,
        icon,
      };
      return stepData;
    })
    .filter((step): step is HomeProgressStep => step !== null);

  if (steps.length === 0 && !asString(get(entry, "onp_summary_title"))) return null;

  const section: HomeProgressStepsData = {
    summaryImageSrc: asString(get(entry, "onp_summary_image")),
    summaryImageAlt: asString(get(entry, "onp_summary_image_alt")),
    summaryTitle: asString(get(entry, "onp_summary_title")),
    summaryDescription: asString(get(entry, "onp_summary_description")),
    ctaLabel: asString(get(entry, "onp_cta_label")),
    ctaUrl: asString(get(entry, "onp_cta_url")),
    steps,
  };

  return section;
}

function mapFeaturedOverlayCards(entry: RawLayoutEntry): HomeFeaturedOverlayCardsData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card) => {
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      const image = asString(get(card, "onp_image"));
      const icon = asString(get(card, "onp_icon"));
      const link = asString(get(card, "onp_link"));
      if (!title && !description && !image) return null;
      const cardData: HomeFeaturedOverlayCard = {
        title,
        description,
        image,
        icon,
        link,
      };
      return cardData;
    })
    .filter((card): card is HomeFeaturedOverlayCard => card !== null);

  if (cards.length === 0 && !asString(get(entry, "onp_heading"))) return null;

  const section: HomeFeaturedOverlayCardsData = {
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    cards,
  };

  return section;
}

function mapFeatureCards4(entry: RawLayoutEntry): HomeFeatureCards4ColData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card, index) => {
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      const image = asString(get(card, "onp_image"));
      const icon = asString(get(card, "onp_icon"));
      const href = asString(get(card, "onp_link"));
      if (!title && !description && !image) return null;
      const cardData: HomeFeatureCard = {
        id: asId(get(card, "onp_id")) ?? `card-${index + 1}`,
        title,
        description,
        image,
        icon,
        href,
      };
      return cardData;
    })
    .filter((card): card is HomeFeatureCard => card !== null);

  if (cards.length === 0 && !asString(get(entry, "onp_heading"))) return null;

  const section: HomeFeatureCards4ColData = {
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    description: asString(get(entry, "onp_description")),
    cards,
  };

  return section;
}

function mapFeatureCards3(entry: RawLayoutEntry): HomeFeatureCards3ColData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card, index) => {
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      const image = asString(get(card, "onp_image"));
      const icon = asString(get(card, "onp_icon"));
      const href = asString(get(card, "onp_link"));
      if (!title && !description && !image) return null;
      const cardData: HomeFeatureCard = {
        id: asId(get(card, "onp_id")) ?? `card-${index + 1}`,
        title,
        description,
        image,
        icon,
        href,
      };
      return cardData;
    })
    .filter((card): card is HomeFeatureCard => card !== null);

  if (cards.length === 0 && !asString(get(entry, "onp_heading"))) return null;

  const section: HomeFeatureCards3ColData = {
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    description: asString(get(entry, "onp_description")),
    cards,
  };

  return section;
}

function mapTechnicalFeaturesRows(entry: RawLayoutEntry): HomeTechnicalFeatureRow[] {
  const rowsRawValue = get(entry, "onp_rows");
  const rowsRaw = Array.isArray(rowsRawValue) ? (rowsRawValue as RawLayoutEntry[]) : [];
  return rowsRaw
    .map((row) => {
      const pointsRawValue = get(row, "onp_points");
      const pointsRaw = Array.isArray(pointsRawValue) ? (pointsRawValue as RawLayoutEntry[]) : [];
      const points = pointsRaw
        .map((point) => {
          const icon = asString(get(point, "onp_icon"));
          const title = asString(get(point, "onp_title"));
          const description = asString(get(point, "onp_description"));
          if (!icon && !title && !description) return null;
          const pointData: HomeTechnicalFeatureRowPoint = {
            icon,
            title,
            description,
          };
          return pointData;
        })
        .filter((point): point is HomeTechnicalFeatureRowPoint => point !== null);

      if (points.length === 0 && !asString(get(row, "onp_row_title"))) return null;

      const rowData: HomeTechnicalFeatureRow = {
        pointLabel: asString(get(row, "onp_point_label")),
        imageSrc: asString(get(row, "onp_image")),
        imageAlt: asString(get(row, "onp_image_alt")),
        rowTitle: asString(get(row, "onp_row_title")),
        rowDescription: asString(get(row, "onp_row_description")),
        points,
        learnMoreHref: asString(get(row, "onp_learn_more_href")),
        learnMoreLabel: asString(get(row, "onp_learn_more_label")),
      };
      return rowData;
    })
    .filter((row): row is HomeTechnicalFeatureRow => row !== null);
}

function mapTechnicalFeatures2Rows(entry: RawLayoutEntry): HomeTechnicalFeatures2RowsData | null {
  const rows = mapTechnicalFeaturesRows(entry);
  if (rows.length === 0) return null;
  return { rows } satisfies HomeTechnicalFeatures2RowsData;
}

function mapTechnicalFeatures3Rows(entry: RawLayoutEntry): HomeTechnicalFeatures3RowsData | null {
  const rows = mapTechnicalFeaturesRows(entry);
  if (rows.length === 0) return null;
  return { rows } satisfies HomeTechnicalFeatures3RowsData;
}

function mapBenefitFeatures2Rows(entry: RawLayoutEntry): HomeBenefitFeatures2RowsData | null {
  const rowsRawValue = get(entry, "onp_rows");
  const rowsRaw = Array.isArray(rowsRawValue) ? (rowsRawValue as RawLayoutEntry[]) : [];
  const rows = rowsRaw
    .map((row) => {
      const pointsRawValue = get(row, "onp_points");
      const pointsRaw = Array.isArray(pointsRawValue) ? (pointsRawValue as RawLayoutEntry[]) : [];
      const points = pointsRaw
        .map((point) => {
          const icon = asString(get(point, "onp_icon"));
          const title = asString(get(point, "onp_title"));
          const description = asString(get(point, "onp_description"));
          if (!icon && !title && !description) return null;
          const pointData: HomeBenefitFeaturePoint = {
            icon,
            title,
            description,
          };
          return pointData;
        })
        .filter((point): point is HomeBenefitFeaturePoint => point !== null);

      if (points.length === 0 && !asString(get(row, "onp_image"))) return null;

      const rowData: HomeBenefitFeaturesRow = {
        imageSrc: asString(get(row, "onp_image")),
        imageAlt: asString(get(row, "onp_image_alt")),
        points,
      };
      return rowData;
    })
    .filter((row): row is HomeBenefitFeaturesRow => row !== null);

  if (rows.length === 0) return null;

  return { rows } satisfies HomeBenefitFeatures2RowsData;
}

function mapPartnerProgram(entry: RawLayoutEntry): HomePartnerProgramData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card) => {
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      const icon = asString(get(card, "onp_icon"));
      const learnMoreUrl = asString(get(card, "onp_learn_more_url"));
      if (!title && !description && !icon) return null;
      const cardData: HomePartnerProgramCard = {
        title,
        description,
        icon,
        learnMoreUrl,
      };
      return cardData;
    })
    .filter((card): card is HomePartnerProgramCard => card !== null);

  if (cards.length === 0 && !asString(get(entry, "onp_heading"))) return null;

  const section: HomePartnerProgramData = {
    kicker: asString(get(entry, "onp_kicker")),
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    cards,
  };

  return section;
}

function mapIconCards4(entry: RawLayoutEntry): HomeServicesData | null {
  const cardsRawValue = get(entry, "onp_cards");
  const cardsRaw = Array.isArray(cardsRawValue) ? (cardsRawValue as RawLayoutEntry[]) : [];
  const cards = cardsRaw
    .map((card) => {
      const title = asString(get(card, "onp_title"));
      const description = asString(get(card, "onp_description"));
      const icon = asString(get(card, "onp_icon_emoji"));
      const learnMoreUrl = asString(get(card, "onp_learn_more_url"));
      if (!title && !description && !icon) return null;
      const cardData: HomeServiceCard = {
        title,
        description,
        icon,
        learnMoreUrl: learnMoreUrl ?? null,
      };
      return cardData;
    })
    .filter((card): card is HomeServiceCard => card !== null);

  if (cards.length === 0 && !asString(get(entry, "onp_heading"))) return null;

  const section: HomeServicesData = {
    kicker: asString(get(entry, "onp_kicker")),
    heading: asString(get(entry, "onp_heading")),
    subheading: asString(get(entry, "onp_subheading")),
    cards,
  };

  return section;
}

function mapSections(raw: unknown): HomePageSection[] {
  if (!Array.isArray(raw)) return [];
  const layout = raw as RawLayout;

  return layout
    .map((entry) => {
      const layoutKey = asString(entry.acf_fc_layout);
      if (!layoutKey) return null;
      switch (layoutKey) {
        case "hero": {
          const data = mapHero(entry);
          return data ? ({ type: "hero", data } satisfies HomePageSection) : null;
        }
        case "services_grid": {
          const data = mapServices(entry);
          return data ? ({ type: "services_grid", data } satisfies HomePageSection) : null;
        }
        case "solutions_tabs": {
          const data = mapSolutions(entry);
          return data ? ({ type: "solutions_tabs", data } satisfies HomePageSection) : null;
        }
        case "one_platform": {
          const data = mapOnePlatform(entry);
          return data ? ({ type: "one_platform", data } satisfies HomePageSection) : null;
        }
        case "growth": {
          const data = mapGrowth(entry);
          return data ? ({ type: "growth", data } satisfies HomePageSection) : null;
        }
        case "integrations": {
          const data = mapIntegrations(entry);
          return data ? ({ type: "integrations", data } satisfies HomePageSection) : null;
        }
        case "process_flow_tabs": {
          const data = mapProcessFlowTabs(entry);
          return data ? ({ type: "process_flow_tabs", data } satisfies HomePageSection) : null;
        }
        case "hero_small_image": {
          const data = mapHeroSmallImage(entry);
          return data ? ({ type: "hero_small_image", data } satisfies HomePageSection) : null;
        }
        case "hero_full_bg": {
          const data = mapHeroFullBg(entry);
          return data ? ({ type: "hero_full_bg", data } satisfies HomePageSection) : null;
        }
        case "page_hero": {
          const data = mapPageHero(entry);
          return data ? ({ type: "page_hero", data } satisfies HomePageSection) : null;
        }
        case "section_title": {
          const data = mapSectionTitle(entry);
          return data ? ({ type: "section_title", data } satisfies HomePageSection) : null;
        }
        case "demo_video_card": {
          const data = mapDemoVideoCard(entry);
          return data ? ({ type: "demo_video_card", data } satisfies HomePageSection) : null;
        }
        case "technical_feature": {
          const data = mapTechnicalFeature(entry);
          return data ? ({ type: "technical_feature", data } satisfies HomePageSection) : null;
        }
        case "video_with_overlay_cards": {
          const data = mapVideoOverlay(entry);
          return data ? ({ type: "video_with_overlay_cards", data } satisfies HomePageSection) : null;
        }
        case "platform_mapping": {
          const data = mapPlatformMapping(entry);
          return data ? ({ type: "platform_mapping", data } satisfies HomePageSection) : null;
        }
        case "progress_steps": {
          const data = mapProgressSteps(entry);
          return data ? ({ type: "progress_steps", data } satisfies HomePageSection) : null;
        }
        case "featured_overlay_cards": {
          const data = mapFeaturedOverlayCards(entry);
          return data ? ({ type: "featured_overlay_cards", data } satisfies HomePageSection) : null;
        }
        case "feature_cards_4col": {
          const data = mapFeatureCards4(entry);
          return data ? ({ type: "feature_cards_4col", data } satisfies HomePageSection) : null;
        }
        case "feature_cards_3col": {
          const data = mapFeatureCards3(entry);
          return data ? ({ type: "feature_cards_3col", data } satisfies HomePageSection) : null;
        }
        case "technical_features_2rows": {
          const data = mapTechnicalFeatures2Rows(entry);
          return data ? ({ type: "technical_features_2rows", data } satisfies HomePageSection) : null;
        }
        case "technical_features_3rows": {
          const data = mapTechnicalFeatures3Rows(entry);
          return data ? ({ type: "technical_features_3rows", data } satisfies HomePageSection) : null;
        }
        case "benefit_features_2rows": {
          const data = mapBenefitFeatures2Rows(entry);
          return data ? ({ type: "benefit_features_2rows", data } satisfies HomePageSection) : null;
        }
        case "partner_program": {
          const data = mapPartnerProgram(entry);
          return data ? ({ type: "partner_program", data } satisfies HomePageSection) : null;
        }
        case "icon_cards_4": {
          const data = mapIconCards4(entry);
          return data ? ({ type: "icon_cards_4", data } satisfies HomePageSection) : null;
        }
        case "bento_box_short": {
          const data = mapBentoBoxShort(entry);
          return data ? ({ type: "bento_box_short", data } satisfies HomePageSection) : null;
        }
        case "bento_box_long": {
          const data = mapBentoBoxLong(entry);
          return data ? ({ type: "bento_box_long", data } satisfies HomePageSection) : null;
        }
        default:
          return null;
      }
    })
    .filter((entry): entry is HomePageSection => entry !== null);
}

export function transformHomeLayout(raw: unknown): HomePageSection[] {
  return mapSections(raw);
}

function fallbackTitle(page: WpPage | null): string {
  if (!page) return "";
  const rendered = page.title?.rendered;
  if (typeof rendered === "string" && rendered.trim()) return rendered.trim();
  return "OnPlexus";
}

type FetchHomePageOptions = {
  previewId?: number;
  previewNonce?: string;
  previewLang?: string;
  lang?: string;
};

type LoadHomePageContext = {
  previewId?: number;
  previewNonce?: string;
  normalizedPreviewLang?: string;
  normalizedLang: string;
  providedLang?: string;
  pathLang?: string;
  prerenderLang?: string;
};

async function loadHomePageContent({
  previewId,
  previewNonce,
  normalizedPreviewLang,
  normalizedLang,
  providedLang,
  pathLang,
  prerenderLang,
}: LoadHomePageContext): Promise<HomePageContent | null> {
  let page: WpPage | null = null;

  if (previewId && previewNonce) {
    page = await getPagePreviewById(previewId, previewNonce, normalizedPreviewLang);
  } else {
    const pageIds = HOMEPAGE_IDS[normalizedLang] ?? HOMEPAGE_IDS[DEFAULT_HOME_CACHE_KEY] ?? [2];

    console.log(
      `[Homepage] Fetching for lang="${normalizedLang}", pageIds=[${pageIds.join(",")}], details:`,
      {
        providedLang,
        pathLang,
        prerenderLang,
        normalizedLang,
        knownIds: HOMEPAGE_IDS,
      },
    );

    const isPrerendering = typeof window !== "undefined" && (window as any).__PRERENDER_WP_BASE__;
    const maxAttempts = isPrerendering ? 3 : 1;

    for (const pageId of pageIds) {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        console.log(`[Homepage] Attempting to fetch by ID ${pageId} for lang="${normalizedLang}" (attempt ${attempt}/${maxAttempts})`);

        try {
          const candidate = await getPageById(pageId, normalizedLang);

          if (candidate) {
            const acf = (candidate as any).acf;
            const acfIsEmptyArray = Array.isArray(acf) && acf.length === 0;
            if (acfIsEmptyArray) {
              console.warn(`[Homepage] Page ${pageId} has ACF as empty array [] - WordPress/ACF configuration issue`);
            }

            const hasContent =
              acf &&
              !acfIsEmptyArray &&
              acf.onp_layout &&
              Array.isArray(acf.onp_layout) &&
              acf.onp_layout.length > 0;

            if (hasContent) {
              console.log(`[Homepage] Found page with content! ID ${pageId} has ${acf.onp_layout.length} sections`);
              page = candidate;
              break;
            } else {
              console.warn(
                `[Homepage] Page ${pageId} exists but has no ACF content (likely due to caching, attempt ${attempt}/${maxAttempts})`,
              );

              if (isPrerendering && attempt < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                continue;
              }

              if (normalizedLang === "fr" && !page) {
                console.warn(
                  `[Homepage] Using page ${pageId} for French despite missing ACF (fallback to avoid failure)`,
                );
                page = candidate;
              }
            }
          } else {
            console.warn(`[Homepage] ID fetch returned null for ${pageId}`);
          }
        } catch (error) {
          console.error(`[Homepage] Error fetching by ID ${pageId}:`, error);
        }

        if (page) break;
      }

      if (page) break;
    }

    if (!page) {
      console.warn(`[Homepage] No content found via IDs, falling back to slug search for lang="${normalizedLang}"`);
      const slugs = resolveHomePageSlugs(normalizedLang);
      console.log(`[Homepage] Trying slugs:`, slugs);
      page = await getPageBySlugs(slugs, normalizedLang);
    }
  }

  if (!page) {
    console.warn(`Failed to fetch homepage content for lang="${normalizedLang}"`);
    return null;
  }

  console.log(`Successfully fetched page ID ${page.id} for lang="${normalizedLang}", title: "${page.title?.rendered}"`);

  const seo = extractSeoMeta(page);
  const sections = mapSections((page.acf as { onp_layout?: unknown })?.onp_layout);
  const targetLocale = normalizedLang === "fr" ? "en" : "fr";
  let alternateRoute: string | null = null;

  if (page.id && targetLocale) {
    try {
      alternateRoute = await getTranslatedRoute(page.id, targetLocale);
    } catch {
      alternateRoute = null;
    }
  }

  if (sections.length === 0) {
    return {
      id: page.id,
      title: fallbackTitle(page),
      sections: [],
      alternateRoute,
      seo,
    } satisfies HomePageContent;
  }

  return {
    id: page.id,
    title: fallbackTitle(page),
    sections,
    alternateRoute,
    seo,
  } satisfies HomePageContent;
}

export async function fetchHomePageContent(options?: FetchHomePageOptions): Promise<HomePageContent | null> {
  const { previewId, previewNonce, previewLang, lang } = options ?? {};

  const prerenderLang = typeof window !== "undefined" ? (window as any).__PRERENDER_LANG__ : undefined;
  const pathLang = typeof window !== "undefined" && window.location?.pathname?.startsWith("/fr") ? "fr" : undefined;

  const normalizedLang = (lang?.trim().toLowerCase() || pathLang || prerenderLang || DEFAULT_HOME_CACHE_KEY).trim();
  const normalizedPreviewLang = previewLang?.trim().toLowerCase() || normalizedLang;
  const isPreview = Boolean(previewId && previewNonce);
  const cacheKey = homeCacheKey(normalizedLang);

  if (!isPreview) {
    const cached = HOME_CACHE.get(cacheKey);
    if (cached) {
      return cached;
    }
    const inFlight = HOME_PROMISES.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }
  }

  const request = loadHomePageContent({
    previewId,
    previewNonce,
    normalizedPreviewLang,
    normalizedLang,
    providedLang: lang,
    pathLang,
    prerenderLang,
  });

  if (!isPreview) {
    HOME_PROMISES.set(cacheKey, request);
  }

  try {
    const data = await request;
    if (!isPreview && data) {
      HOME_CACHE.set(cacheKey, data);
    }
    return data;
  } finally {
    if (!isPreview) {
      HOME_PROMISES.delete(cacheKey);
    }
  }
}

export type { HomePageSection as HomeSection };

export type FlexiblePageContent = {
  id: number;
  title: string;
  sections: HomePageSection[];
};

export type FetchFlexiblePageOptions = {
  lang?: string;
  previewId?: number;
  previewNonce?: string;
  previewLang?: string;
  pathSegments?: string[];
};

export async function fetchFlexiblePageContent(
  slugs: string[],
  options?: FetchFlexiblePageOptions,
): Promise<FlexiblePageContent | null> {
  const { lang, previewId, previewNonce, previewLang, pathSegments } = options ?? {};

  let page: WpPage | null = null;

  if (previewId && previewNonce) {
    page = await getPagePreviewById(previewId, previewNonce, previewLang ?? lang);
  } else {
    if (Array.isArray(pathSegments) && pathSegments.length > 0) {
      page = await getPageByPath(pathSegments, lang);
    }
    if (!page) {
      page = await getPageBySlugs(slugs, lang);
    }
  }

  if (!page) return null;

  const sections = mapSections((page.acf as { onp_layout?: unknown })?.onp_layout);
  return {
    id: page.id,
    title: fallbackTitle(page),
    sections,
  } satisfies FlexiblePageContent;
}
